{
  "_note": "Global rules. Keys beginning with an underscore are comments; JSON has none.",

  "movement": {
    "_note": "Movement points are integers in units of 1/scale, so a scale of 3 gives thirds. Integers rather than fractions because floating point in the game state makes save hashes drift between machines.",
    "scale": 3,
    "_allowSingleTileWhenDepleted": "A unit with any movement left may enter any legal tile, spending everything it has. Without this a 1-move unit could never enter hills.",
    "allowSingleTileWhenDepleted": true,
    "_roadCost": "Move points to step from one road (or city) to another. With scale 3, a cost of 1 is a third of a move: three road tiles for one movement point.",
    "roadCost": 1
  },

  "vision": {
    "_note": "Sight is a plain hex radius from each unit. Terrain does not block line of sight yet.",
    "elevationBonus": 1,
    "_cityRadius": "How far a city sees. Keeps a civilisation from going blind at home when its garrison leaves.",
    "cityRadius": 2,
    "highGroundTerrain": ["hills", "mountains"]
  },

  "start": {
    "_note": "How a civilisation's opening position is chosen. Scores the tiles a new city could work, not just the tile it stands on.",
    "ringRadius": 2,
    "polarMargin": 0.12,
    "minSeparation": 8,
    "foodWeight": 3,
    "productionWeight": 2,
    "goldWeight": 1,
    "resourceBonus": 4,
    "freshWaterBonus": 12,
    "coastBonus": 6,
    "waterTilePenalty": 1.5
  },

  "cities": {
    "_note": "Growth and tile working. foodPerCitizen is what a citizen eats; growth needs growthBase + growthPerPop x population food in the store.",
    "startingPopulation": 1,
    "foodPerCitizen": 2,
    "growthBase": 15,
    "growthPerPop": 6,
    "workRadius": 2,
    "startingBorderRadius": 1,
    "minSpacing": 3,
    "_centreMinimums": "A city centre is never wholly barren, however bad the tile under it.",
    "centreMinFood": 1,
    "centreMinProduction": 1,
    "_centreMinGold": "A settlement is a market by definition. Without this an inland city earns literally nothing for ever and cannot keep a single building standing.",
    "centreMinGold": 1,
    "_riverGoldBonus": "Rivers carry trade. This is what stops commerce being a thing only coastal cities have, and gives rivers a reason to matter beyond fresh water.",
    "riverGoldBonus": 1,
    "workWeights": { "food": 3, "production": 2, "gold": 1 },
    "_capFoodWeight": "The food weight the governor uses instead when a city is at its size limit with a full store: food beyond what the citizens eat would only be thrown away, so the citizens work shields and trade instead. The governor still keeps the city fed (ADR-0069).",
    "capFoodWeight": 0,
    "_baseSizeCap": "How large a city grows on its own wells. An aqueduct lifts it. This is the first real brake on growth — before it existed a city ran to twelve by turn forty.",
    "baseSizeCap": 8,
    "_switchPenalty": "Changing what a city is building, between a unit and a building or the other way, loses this share of the shields already stored. Without it a city can bank production into something cheap and swap to something expensive for nothing. Switching within a category is free, as is reordering the queue behind whatever is being built, and as is finishing one thing and starting the next.",
    "switchPenalty": 0.5,
    "_autoSpecialist": "Citizens who work no tile become specialists (specialists.json). A city left on Auto makes as many entertainers as it needs to stay out of disorder, and makes the rest of them this.",
    "autoSpecialist": "scholar"
  },

  "economy": {
    "_note": "Commerce — the gold yield of the tiles a city works — is split between the treasury and research by each civilisation's science rate. What is not researched becomes gold.",
    "startingTreasury": 50,
    "_insolvency": "When a civilisation cannot pay, it sells a building rather than running a negative treasury. Debt with no consequence is not a constraint.",
    "sellBuildingRefund": 0.25,
    "_freeUnitsPerCity": "How many units' upkeep each city supports for free (ADR-0052) is now the government's freeUnitsPerCity (governments.json, ADR-0059)."
  },

  "research": {
    "_note": "A technology costs baseCost x costGrowth^depth, where depth is its longest chain of prerequisites (a root is 0). The whole pace of the game lives in these two numbers.",
    "_calibration": "Set for a long game of about 500 turns (Mark: 'the more the better'). 30 and 1.45 keep the opening moving — a capital of one or two citizens makes its first discovery in about twenty turns, and each civilisation starts with two — while making deep technologies steep, so the later eras stretch out. Measured with four AI civilisations on a standard map (Phase 6b, libraries and all): about 24 technologies by turn 100, 38 by 200, 50 by 400; classical around turn 90, medieval around 130, renaissance around 300-370. The late pace is bound by an economy that plateaus (AI-0's cities stop at four and fall into disorder at the cap) and should be re-measured when Phase 12 adds the rest of it.",
    "baseCost": 30,
    "costGrowth": 1.56,
    "_late": "Phase 13a: past lateDepth each step costs lateGrowth times the last instead of costGrowth, so the industrial and modern eras arrive within a 500-turn game (leader modern by about turn 350).",
    "lateDepth": 10,
    "lateGrowth": 1.35,
    "_rates": "The science slider, as a percentage of commerce. It moves in rateStep steps up to maxRate; governments will lower the ceiling in Phase 10.",
    "startingRate": 50,
    "rateStep": 10,
    "maxRate": 100,
    "_scholars": "Every city also produces science from its own people: one per citizensPerScience citizens, rounded up, whatever the slider says. Measured, not guessed — with commerce alone the simulated AI civilisations were running at 0 science after 400 turns, because their commerce barely covered upkeep and a science rate above zero bankrupted them. Commerce is thin by design (it has to be won from rivers, coast and roads), so knowledge needs a floor that grows with the empire rather than with its treasury.",
    "citizensPerScience": 2
  },

  "drag": {
    "_note": "Administrative drag: the original take on corruption. Gold and production lost to distance from the seat of government, so the SHAPE of an empire matters, not only its size. Roads soften it in Phase 5b; a courthouse softens it now.",
    "lossPerTile": 0.04,
    "maxLoss": 0.7,
    "capitalFreeRadius": 2
  },

  "contentment": {
    "_note": "Citizens beyond contentBase are discontented unless something buys them back. A city with more discontented than content citizens falls into disorder and produces nothing at all.",
    "_measured": "At contentBase 4 disorder needed population above 10, while the size cap is 8 — so contentment never bit at all. Measured with the simulation harness, not guessed. At 3, an ungarrisoned city without a temple falls into disorder at size 7, while a capital (palace) or a garrisoned city stays content to the cap. That asymmetry is deliberate: the capital is the easiest place to govern.",
    "contentBase": 3,
    "disorderProductionMultiplier": 0
  },

  "work": {
    "_note": "Workers. An improvement takes turnsToBuild worker-turns; two workers on one tile halve the wait, which is the only reason to ever build a second.",
    "_roadDrag": "Roads reduce drag for the city that owns the tile they stand on. Full relief needs a road on the city itself.",
    "maxWorkersPerTile": 4
  },

  "ai": {
    "_note": "AI-0 weights. Every decision the AI makes is a score built from these — no behaviour is written as a branch in code. AI-1 in Phase 8 tunes and extends this block rather than rewriting the module.",

    "_search": "How far the AI will look, in tiles. Larger is smarter and slower.",
    "settleSearchRadius": 12,
    "settleDistancePenalty": 1.5,
    "exploreSearchRadius": 14,
    "exploreDistancePenalty": 0.4,

    "_production": "What a city is worth building. Expansion leads early and tails off as the empire fills.",
    "settlerValue": 100,
    "settlerPerCityPenalty": 10,
    "settlerPerPendingPenalty": 45,
    "workerValue": 62,
    "workerPerWorkerPenalty": 22,
    "defenderValue": 50,
    "defenderPerUnitPenalty": 14,
    "scoutValue": 30,

    "buildingBase": 40,
    "buildingGrowthWeight": 30,
    "buildingProductionWeight": 9,
    "buildingGoldWeight": 7,
    "buildingContentmentWeight": 11,
    "buildingSizeCapWeight": 3,
    "buildingDragWeight": 24,
    "buildingScienceWeight": 0.5,
    "_buildingCulture": "Culture is valued per point a turn. A wonder's civEffects are valued as the same effect in every city, times wonderCivShare — less than in full, because the AI should not empty a city's queue for a wonder it may lose.",
    "buildingCultureWeight": 6,
    "wonderCivShare": 0.5,

    "_research": "A technology's appeal is its cost discounted by what it unlocks: cost / (1 + techUnlockWeight x unlocks). At 0.6, a technology that unlocks two things is worth researching at nearly twice the cost of one that unlocks nothing.",
    "techUnlockWeight": 0.6,

    "_costPenalty": "Everything is discounted by its cost, so a poor city builds cheap things.",
    "costPenalty": 0.35,

    "_ai1": "AI-1 (Phase 8, ADR-0053). Personalities multiply these and the weights above.",
    "_personalityTwist": "Chance, per AI civilisation per game, of a personality other than the one it leans to.",
    "personalityTwist": 0.3,
    "_research": "What a technology unlocks makes it worth researching: techUnlockWeight per unlock, times the category weight — military units, science buildings, gold buildings, and growth (farms and the like, granaries, aqueducts).",
    "techMilitaryWeight": 1.0,
    "techScienceWeight": 1.0,
    "techGoldWeight": 1.0,
    "techGrowthWeight": 1.0,
    "_government": "AI government choice (ADR-0059): each government's aiValue for the personality, less governmentWarPenalty x its warWeariness while at war. The AI changes only when the best beats the current by governmentSwitchMargin, and not within governmentMinTurns of the last change. techGovernmentWeight is what unlocking a government adds to a technology's appeal.",
    "governmentSwitchMargin": 4,
    "governmentWarPenalty": 8,
    "governmentMinTurns": 30,
    "techGovernmentWeight": 4,
    "_workers": "Workers are worth workerValue less workerPerWorkerPenalty each, plus workerPerCityBonus for every city. A road is worth roadValue on a tile that links two cities, as a share of a worked tile's gain.",
    "workerPerCityBonus": 14,
    "roadValue": 1.0,
    "_resources": "A worker building a road that joins a needed strategic resource to a city scores resourceRoadValue, against a worked tile's gain (ADR-0063). An AI lacking a resource it needs offers resourceBuyPerTurn gold a turn for it.",
    "resourceRoadValue": 6,
    "resourceBuyPerTurn": 3,
    "_threat": "Threat: enemy soldiers it can see within threatRadius of a city, by attack, count in full at war and at threatPeaceShare at peace. Each threatPerDefender of it asks for one more defender in that city, up to maxGarrison.",
    "threatRadius": 3,
    "threatPeaceShare": 0.25,
    "threatPerDefender": 3,
    "maxGarrison": 4,

    "_military": "What an army is worth building: defenderValue less defenderPerUnitPenalty for each fighting unit already fielded, plus unitStrengthWeight x its better of attack and defence, plus warUnitBonus while at war. Cities keep garrisonPeace defenders at peace and garrisonWar at war.",
    "unitStrengthWeight": 6,
    "warUnitBonus": 80,
    "_unguardedBonus": "Extra value of a fighting unit to a city with no defender standing in it.",
    "unguardedBonus": 40,
    "garrisonPeace": 1,
    "garrisonWar": 1,

    "_war": "AI-0 starts a war only when it is much stronger: its military at least warStrengthRatio times the rival's, at least warMinimumUnits fighting units, not before warEarliestTurn, and then only on a warChancePerTurn roll so it is not a clock. Military strength is public — every civilisation's advisers can count soldiers — and the same figure is shown to the player.",
    "warStrengthRatio": 1.5,
    "warMinimumUnits": 3,
    "warEarliestTurn": 85,
    "warChancePerTurn": 0.08,
    "_warPrep": "Arming (ADR-0071): from warPrepLead turns before warEarliestTurn, a civilisation at peace that has found a rival it is not yet warStrengthRatio times stronger than values attacking land units warPrepBonus more. Personalities scale it: a militarist arms hard, a builder hardly at all.",
    "warPrepBonus": 20,
    "warPrepLead": 10,
    "_treatyWar": "A war that would break a standing treaty is that much less likely (ADR-0073): the AI keeps its word.",
    "treatyWarFactor": 0.03,
    "_wonderHeld": "Each wonder a civilisation already holds makes the next worth wonderPerHeldPenalty less to its AI (ADR-0073).",
    "wonderPerHeldPenalty": 50,
    "_wonderCost": "A wonder is discounted by its cost at wonderCostPenalty rather than costPenalty, and is worth wonderBase for itself: later wonders cost several times the early ones (ADR-0073).",
    "wonderCostPenalty": 0.15,
    "wonderBase": 40,
    "_techSale": "An AI may sell a technology for techSalePrice of its research cost, in gold or gold a turn (ADR-0073). Technology deals come before resource deals, with partners Cautious or better.",
    "techSalePrice": 0.6,
    "_gpt": "An AI promises gold a turn only up to gptIncomeShare of what it could spare with no science, after what it already owes; and it sets its science rate with its payments counted (ADR-0073).",
    "gptIncomeShare": 0.5,
    "_attack": "Attack only at these odds of winning or better. Higher is more cautious. When siegeUnits or more of its fighting units stand next to the target it accepts siegeOdds instead: each attack wears the defender down for the next.",
    "attackMinimumOdds": 0.6,
    "siegeUnits": 3,
    "siegeOdds": 0.3,
    "_peace": "Peace (Phase 13c, ADR-0071): a war ends when it has done what it was for (the plan's aim cities taken), when it is clearly being lost (lost more than peaceLossRatio times what it inflicted, and at least peaceLossFloor shields), when the enemy has grown stronger than peaceWhenStrengthBelow times its own, when war weariness makes peaceWearyCitizens discontented in every city, or when nothing has been taken for stalemateTurns. Not on a timer. Never before minimumWarTurns.",
    "minimumWarTurns": 15,
    "peaceWhenStrengthBelow": 1.0,
    "peaceLossRatio": 1.5,
    "peaceLossFloor": 60,
    "peaceWearyCitizens": 2,
    "stalemateTurns": 50,
    "_campaign": "War plans (Phase 13c, ADR-0071). A war is for warAims enemy cities, nearest the AI's own cities first (a capital counts aimCapitalPenalty tiles further). The army gathers at the home city nearest the first aim until armyPerDefender soldiers per defender expected (armyGuessDefenders when it cannot see in), and at least armyMinimum, stand within gatherRadius; after gatherMaxTurns it sets out with gatherShare of its army (at least armyMinimum). Only land units at least as good in attack as in defence, and siege engines, march; latecomers follow. It marches together, nobody more than marchSpread tiles ahead of the hindmost, and pulls back to gather again when fewer than regroupShare of those who set out are left.",
    "warAims": 2,
    "aimCapitalPenalty": 4,
    "armyMinimum": 4,
    "armyPerDefender": 2,
    "armyGuessDefenders": 2,
    "gatherRadius": 1,
    "gatherMaxTurns": 10,
    "gatherShare": 0.6,
    "_siegeLead": "Siege engines lead (ADR-0072): an army that can build them takes siegeArmyShare of its number as siege engines, and never marches on a walled city without at least one. While a war plan is short of soldiers or siege engines, cities value those armyNeedBonus more.",
    "siegeArmyShare": 0.3,
    "armyNeedBonus": 60,
    "_marchResolve": "While its army is on the march, a side counts itself outmatched only below marchResolve times the usual strength line, and bears one more weary citizen before it wants peace (ADR-0072).",
    "marchResolve": 0.7,
    "_seaExplore": "Exploring by sea (ADR-0072): ships look seaExploreReach times as far as soldiers for unexplored water, and an anchored explorer looks again every seaExploreRetry turns. A civilisation that has not met every rival and has no ship able to cross an ocean values one at seaScoutValue.",
    "seaExploreReach": 3,
    "seaExploreRetry": 10,
    "regroupShare": 0.4,
    "marchSpread": 1,
    "_peaceTerms": "Peace with terms (ADR-0071). Giving up a city costs cityKeepFactor times its worth. A side losing a war will pay up to the worth of the city the enemy is marching on, or peaceGoldShare of its treasury; a side that means to fight on wants peacePriceShare of the city it is marching on (peaceBasePrice when it has none). A side whose war is done, or stuck, takes plain peace. Gold and technologies offered for peace count at peaceTradeShare of their worth — a war is fought for land — and a peace offer carries at most one technology.",
    "cityKeepFactor": 1.25,
    "peaceGoldShare": 0.5,
    "peacePriceShare": 0.75,
    "peaceBasePrice": 60,
    "peaceTradeShare": 0.4,
    "_aiDiplomacy": "AI-2 (Phase 10b, ADR-0060). Each turn an AI may, with offerChance, make one civilisation it has met an offer (a technology swap or sale, open borders, an alliance), waiting aiOfferCooldown turns before offering the same civilisation again. It demands tribute with demandChance from a civilisation it outmatches by demandStrengthRatio, asking demandGoldShare of its treasury (or a technology). It gives in to a demand from one that outmatches it by yieldStrengthRatio. It values open borders at openBordersValue, a sound alliance at allianceValue, and asks dangerTechFactor times more for knowledge that helps a stronger rival's army.",
    "offerChance": 0.25,
    "demandChance": 0.04,
    "demandStrengthRatio": 2.0,
    "demandGoldShare": 0.3,
    "yieldStrengthRatio": 1.6,
    "openBordersValue": 15,
    "allianceValue": 40,
    "dangerTechFactor": 1.5,

    "_navy": "Ships. The first ship of a coastal civilisation explores (seaScoutValue). A transport is worth transportValue while it is at war with a rival it cannot reach by land and has none. Warships are otherwise not built by AI-0.",
    "seaScoutValue": 70,
    "transportValue": 90,
    "_upgrades": "Upgrade units standing in its cities while the treasury keeps at least upgradeReserve gold afterwards. While any unit could be upgraded and the treasury cannot pay for it, the science rate is held upgradeSavingSteps steps below what it could afford, so gold builds up.",
    "upgradeReserve": 25,
    "upgradeSavingSteps": 2,
    "_scouts": "Each scout already fielded makes another worth scoutPerScoutPenalty less.",
    "scoutPerScoutPenalty": 25,
    "buildingVeteranWeight": 25,
    "_treasuryRunway": "Phase 13a: gold above upgradeReserve is spent on science over treasuryRunway turns.",
    "treasuryRunway": 20,
    "_siege": "Siege and walls (ADR-0064). Siege engines are at most siegeShare of a civilisation's land army (at least one), and are worth siegePeacePenalty less at peace. Walls are worth buildingWallsWeight a percent of defence, at wallsPeaceShare of that at peace.",
    "siegeShare": 0.25,
    "siegePeacePenalty": 30,
    "buildingWallsWeight": 0.4,
    "wallsPeaceShare": 0.25,
    "_air": "Aircraft (ADR-0065) are worth airValue less airPerUnitPenalty for each already flying, plus their strength; at most airShare of the army, and worth airPeacePenalty less at peace; anti-aircraft is worth buildingAntiAirWeight a percent, at wallsPeaceShare of that at peace or when no enemy flies.",
    "airShare": 0.2,
    "airValue": 120,
    "airPerUnitPenalty": 30,
    "interceptorBonus": 100,
    "_carriers": "An aircraft carrier (ADR-0066) is worth carrierValue to a civilisation at war overseas that has aircraft and no carrier; it holds station carrierStandoff tiles from the enemy city.",
    "carrierValue": 140,
    "carrierStandoff": 3,
    "_navy": "Warships (ADR-0067): one is wanted for every transport or carrier, plus homeFleet at war, each worth warshipValue. They escort a convoy without one, else hunt enemy ships within huntRadius they can beat at attackMinimumOdds, else guard the nearest port.",
    "warshipValue": 90,
    "homeFleet": 1,
    "huntRadius": 6,
    "airPeacePenalty": 40,
    "buildingAntiAirWeight": 0.6
  },

  "combat": {
    "_note": "Classic rounds (ADR-0048). Attacker strength is its attack; defender strength is its defence times (100 + every bonus below that applies, in percent) / 100. Each round the attacker lands a hit with probability A / (A + D); a hit takes damagePerRound hit points. The fight ends when one side reaches zero, and the loser dies. The odds shown before an attack are computed exactly from the same numbers.",
    "damagePerRound": 3,
    "_bonuses": "Percent, added together with the tile's terrain and feature defenceBonus.",
    "fortifyBonus": 25,
    "riverBonus": 25,
    "cityBonus": 50,
    "largeCitySize": 8,
    "largeCityBonus": 25,
    "_attackCost": "Whole movement points an attack spends. A fast unit can strike twice.",
    "attackCost": 1,
    "_healing": "Hit points recovered at the start of a turn by a unit that did not move or fight in the last one.",
    "healField": 1,
    "healOwnTerritory": 2,
    "healCity": 3,
    "_besiege": "A city with besiegeTiles or more of the tiles around it held by enemy land soldiers is besieged (ADR-0072): its defenders do not heal and it gathers no food.",
    "besiegeTiles": 2,
    "_capture": "A captured city loses captureCitizensLost citizens (never its last) and each building has buildingLossChance of being destroyed. The palace is always lost; the old owner's seat moves to its largest remaining city.",
    "captureCitizensLost": 1,
    "buildingLossChance": 0.34,
    "_ranks": "Experience (ADR-0050). Every unit starts at the first rank (the second if trained with a barracks). Winning a fight promotes it to the next rank with promotionChance[current rank]. Each rank adds hpBonus hit points, which is what makes a veteran harder to kill.",
    "ranks": [
      { "name": "Green", "hpBonus": 0 },
      { "name": "Veteran", "hpBonus": 3 },
      { "name": "Elite", "hpBonus": 6 }
    ],
    "promotionChance": [0.5, 0.25],
    "_upgrade": "Upgrading a unit in one of your cities costs upgradeBase gold plus upgradePerShield for every shield the new unit costs more than the old. It spends the unit's moves for the turn and keeps its rank and its share of health.",
    "upgradeBase": 10,
    "upgradePerShield": 2,
    "_siege": "Siege (ADR-0064). A unit with the siege ability attacking a city ignores its defenders' City, Large city, Walls and Fortified bonuses; terrain still counts. A unit with bombard fires shots at the best defender of an enemy tile within range, each landing with chance strength / (strength + defence) for damagePerRound, never taking it below bombardMinHp, at no risk to itself; it spends the unit's turn.",
    "bombardMinHp": 1,
    "_air": "Aircraft (ADR-0065) are based in cities and fly one mission a turn within airRange: a strike or a rebase. A strike may first be met by an enemy interceptor based within its own airRange of the target: up to interceptRounds rounds of fighting, the interceptor attacking. Then a city's anti-aircraft fires antiAirShots shots, each landing with the building's antiAir percent for damagePerRound. A striker still flying fights the best defender to the death — city defences count, walls do not.",
    "interceptRounds": 4,
    "antiAirShots": 2
  },

  "diplomacy": {
    "_note": "Before full diplomacy (Phase 10) there are only war and peace. Everyone starts at peace; attacking a rival's unit or city declares war. After peace is made, neither side may declare war again for truceTurns. A refused peace offer cannot be repeated for peaceOfferCooldown turns.",
    "truceTurns": 10,
    "peaceOfferCooldown": 8,
    "_cityLossValue": "How much losing a city counts against a side, in the same shields as a lost unit's cost, when judging how a war is going.",
    "cityLossValue": 120,
    "_cityValue": "What a city is worth when it is a term of peace (ADR-0071): cityValuePerCitizen gold for each citizen and cityBuildingShare of what its buildings cost.",
    "cityValuePerCitizen": 60,
    "cityBuildingShare": 1,
    "_treaty": "Making peace signs a treaty for treatyTurns. War cannot be declared during the truce; after it, declaring war before the treaty ends breaks it, which costs reputation.brokeTreaty with everyone (ADR-0060).",
    "treatyTurns": 30,
    "_deals": "Gold per turn is paid for paymentTurns. A deal offered to the human player waits offerTurns before it is withdrawn.",
    "paymentTurns": 20,
    "offerTurns": 2,
    "aiOfferCooldown": 10,
    "_resources": "A strategic resource traded in a deal is shared for paymentTurns (ADR-0063). Receiving one that lets the judge build soldiers it cannot build now is worth resourceValuePerTurn a turn, otherwise resourceIdlePerTurn; giving one costs resourceCostPerTurn a turn, more when it arms a stronger rival.",
    "resourceValuePerTurn": 4,
    "resourceIdlePerTurn": 0.5,
    "resourceCostPerTurn": 1,
    "_memories": "What one civilisation remembers of another, and how it feels about it: value at first, fading to nothing over turns. max caps what many memories of one kind can add up to.",
    "memories": {
      "warDeclared": { "value": -30, "turns": 150, "label": "declared war on us" },
      "cityTaken": { "value": -18, "turns": 200, "max": 50, "label": "took our cities" },
      "peaceMade": { "value": 6, "turns": 60, "label": "made peace with us" },
      "dealMade": { "value": 3, "turns": 60, "max": 12, "label": "trades with us" },
      "gift": { "value": 1, "turns": 80, "max": 15, "label": "has been generous" },
      "tributeReceived": { "value": 6, "turns": 60, "label": "gave in to our demands" },
      "tributePaid": { "value": -8, "turns": 80, "label": "forced tribute from us" },
      "demandRefused": { "value": -12, "turns": 60, "label": "refused our demands" },
      "allied": { "value": 12, "turns": 100, "label": "fought beside us" },
      "deserted": { "value": -20, "turns": 120, "label": "made a separate peace" }
    },
    "_attitude": "Attitude: memories, plus what is true now — war, a shared enemy, the same government, open borders, an alliance, how hard their border presses on ours (per frontier tile, to a cap), and their reputation. Bands, worst first: an attitude below a band's 'below' falls in it. dealMargin is how much more an AI wants to get than it gives (null: it will not deal); warFactor scales its chance of choosing this civilisation for a war.",
    "attitude": {
      "atWar": -40,
      "sharedEnemy": 10,
      "sameGovernment": 4,
      "openBorders": 3,
      "alliance": 10,
      "borderPressurePerTile": -0.4,
      "borderPressureMax": -12,
      "reputationWeight": 1,
      "bands": [
        { "name": "Furious", "below": -25, "dealMargin": null, "warFactor": 3 },
        { "name": "Annoyed", "below": -8, "dealMargin": 1.4, "warFactor": 1.5 },
        { "name": "Cautious", "below": 8, "dealMargin": 1.15, "warFactor": 1 },
        { "name": "Pleased", "below": 25, "dealMargin": 1.0, "warFactor": 0.4 },
        { "name": "Friendly", "below": null, "dealMargin": 0.9, "warFactor": 0.1 }
      ]
    },
    "_reputation": "Breaking your word costs standing with everyone: brokeTreaty for declaring war during a treaty, defaulted for stopping a payment. It recovers by recoveryPerTurn.",
    "reputation": { "brokeTreaty": -30, "defaulted": -15, "recoveryPerTurn": 0.4 }
  },

  "culture": {
    "_note": "Culture (ADR-0057). Each city adds its buildings' culture to its own total every turn. Its border radius starts at cities.startingBorderRadius and grows by one at each threshold the total passes. Where two borders overlap, a tile goes to the city with the most influence on it — (culture + 1) / distance squared — except that a city always holds its own tile and its first ring.",
    "borderThresholds": [20, 300, 3000]
  },

  "government": {
    "_note": "Governments are in governments.json (ADR-0059). Every civilisation starts under 'start'. Changing government means anarchyTurns turns under 'anarchy' first.",
    "start": "despotism",
    "anarchy": "anarchy",
    "anarchyTurns": 2
  },

  "weariness": {
    "_note": "War weariness (ADR-0059). Every unit or city a civilisation loses in a war adds its value in shields (a city counts diplomacy.cityLossValue). Every city then has one more discontented citizen per shieldsPerCitizen of weariness, times the government's warWeariness, up to maxCitizens. It fades by decayAtWar a turn while at war, and by decayAtPeace once at peace with everyone.",
    "shieldsPerCitizen": 120,
    "maxCitizens": 4,
    "decayAtWar": 0.03,
    "decayAtPeace": 0.2
  },

  "score": {
    "_note": "Score (ADR-0061), shown all game on the Rivals screen: citizens, land held, technologies, wonders and culture, each times its weight, plus a victory bonus that is larger the earlier the win (earlyWinPerTurn for every turn before earlyWinReference). historyEvery: how often the game's history is sampled for the end-of-game charts.",
    "perCitizen": 1,
    "perTile": 0.2,
    "perTech": 2,
    "perWonder": 10,
    "perCulture": 0.002,
    "victory": 200,
    "earlyWinReference": 500,
    "earlyWinPerTurn": 1,
    "historyEvery": 5
  },

  "victory": {
    "_note": "The ways a game can be won (ADR-0061). Only conquest for now — the last civilisation standing — by Mark's choice; others can be added here as the engine learns them. There is no time limit.",
    "conditions": ["conquest"]
  },

  "resources": {
    "_note": "Strategic resources (ADR-0063). A city has a resource when a revealed deposit inside its civilisation's borders is joined to it — by roads (a city's tile counts as one) and rivers through its own or unclaimed land, or by sea between its coastal cities once seaTech is known. A resource traded in reaches every city.",
    "seaTech": "seafaring"
  },

  "autosave": {
    "enabled": true,
    "slots": 3
  }
}
